import { queriesEngagements } from "@query";
import {
  useWSModal,
  WSActions,
  WSDivider,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionCreateRequest,
  IRequirementDefinitionResponse
} from "@wingspanhq/payments/dist/interfaces";

import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationCreateRequirementDefinition } from "../../../../query/requirementDefinitions/mutation/useMutationCreateRequirementDefinition";

type ConfirmCreateRequirementModalProps = {
  onClose: () => void;
  onSuccess: (requirement: IRequirementDefinitionResponse) => void;
  engagementId?: string;
} & IRequirementDefinitionCreateRequest;

const ConfirmCreateRequirementModal: React.FC<
  ConfirmCreateRequirementModalProps
> = ({ onClose, engagementId, onSuccess, ...requirementRequest }) => {
  const [createRequirement, createRequirementMeta] =
    useMutationCreateRequirementDefinition();
  const [addRequirementToEngagement, addRequirementToEngagementMeta] =
    queriesEngagements.addEngagementRequirement.useMutation();
  return (
    <WSList gap="XL">
      <WSInfoBox>
        <WSText.ParagraphXs mb="XS" weight="medium">
          Confirm requirement creation
        </WSText.ParagraphXs>

        <WSText.ParagraphXs weight="book">
          Once a requirement is created, it cannot be edited.
          <br />
          If the requirement needs to be changed, a new version must be created
          and configured.
          <br />
          <br />
          Existing requirements can be previewed, attached, or removed from
          engagement type at any time.
        </WSText.ParagraphXs>
      </WSInfoBox>

      <WSDivider />

      <WSErrorMessage
        contextKey="Other"
        error={
          createRequirementMeta.error || addRequirementToEngagementMeta.error
        }
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Primary",
            label: "Create requirement",
            loading:
              createRequirementMeta.isLoading ||
              addRequirementToEngagementMeta.isLoading,
            name: "createRequirementDefinition",
            onClick: async () => {
              await createRequirement(requirementRequest, {
                onSuccess: async requirement => {
                  if (engagementId) {
                    // update engagement with new requirement
                    await addRequirementToEngagement(
                      {
                        engagementId,
                        engagementRequirementCreate: {
                          requirementDefinitionId:
                            requirement.requirementDefinitionId
                        }
                      },
                      {
                        onSuccess: () => {
                          onSuccess(requirement);

                          onClose();
                        }
                      }
                    );
                  } else {
                    onSuccess(requirement);

                    onClose();
                  }
                }
              });
            }
          },
          {
            kind: "Secondary",
            label: "Cancel",
            onClick: onClose
          }
        ]}
      />
    </WSList>
  );
};

export function useConfirmCreateRequirementModal() {
  return useWSModal(ConfirmCreateRequirementModal, {
    size: "S",
    title: "Create requirement"
  });
}
